/**
 * AI 可见性页面 — AiVisibility（全景矩阵版，对齐 matrix.html）。
 *
 * 数据源：
 * - GET /api/ai-visibility?company_id=        汇总卡 + BAVS 五维（保留层）
 * - GET /api/alerts/summary                   顶部波动指示 3 卡（fluctuation + p0/p1 计数）
 * - GET /api/ai-visibility/matrix?brand_id    话题 × 平台 热力矩阵（批 4 §7）
 * - GET /api/ai-visibility/trend              选中话题 7 日趋势（recharts LineChart）
 *
 * 钻取态：URL 参数 topic/platform/highlight=1（预警 AR1 钻入）→ 自动选中该话题
 * + 该平台列高亮。matrix/trend 端点未就绪时一律 EmptyState 降级，不红屏。
 *
 * 保留：汇总卡层 + BAVS 维度 + getScoreColor；退役：底部明细 DataTable
 * （其数据列已被矩阵覆盖）。
 *
 * 展示门禁（指标字典 §13.3）：BAVS 总分/评级卡及未接入维度（答案份额/
 * 信源引用率/情感占比）无真实数据源 → 一律不渲染，禁止"待产出"伪装。
 */
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  useTheme,
} from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import UpdateCapsule from "../components/UpdateCapsule";
import HeatMatrix from "../components/HeatMatrix";
import Badge from "../components/Badge";
import {
  getAiVisibility,
  getVisibilityMatrix,
  getVisibilityTrend,
} from "../api/visibility";
import { getAlertSummary } from "../api/alerts";
import { useCompanyStore } from "../store/companyStore";
import { formatDateTime } from "../utils/format";
import type {
  AiVisibility,
  AlertSummary,
  TrendPoint,
  VisibilityMatrix,
} from "../types";

/** 五维强弱状态颜色（PRD §2.2.1：红<60/橙60-70/绿≥80）。 */
function getScoreColor(score: number | null): string {
  if (score === null) return "text.disabled";
  if (score < 60) return "error.main";
  if (score < 80) return "warning.main";
  return "success.main";
}

/** 格式化分值（门禁 §13.3：无数据维度在渲染前已被过滤，不会走到这里）。 */
function formatScore(score: number): string {
  return score.toFixed(1);
}

/** 汇总卡片。 */
function SummaryCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1 }}>
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: 24, fontWeight: 600, color: color || "text.primary" }}
        >
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/** BAVS 维度定义。
 * 已接入真实数据的四维：品牌提及率 / 首推率 / 信源引用 / 情感占比。
 * 排名位置（ranking 上传端恒为 None，无数据源）→ 不渲染（展示门禁 §13.3）。 */
const BAVS_DIMENSIONS = [
  { name: "品牌提及率", weight: "25%" },
  { name: "首推率（答案份额）", weight: "25%" },
  { name: "信源引用量", weight: "20%" },
  { name: "正面情感占比", weight: "15%" },
];

export default function AiVisibilityPage() {
  const muiTheme = useTheme();
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const [searchParams, setSearchParams] = useSearchParams();
  const drillTopic = searchParams.get("topic");
  const drillPlatform = searchParams.get("platform");
  const isDrill = searchParams.get("highlight") === "1";

  const [items, setItems] = useState<AiVisibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 批 4：矩阵 / 趋势 / 波动指示状态（端点未就绪一律降级，不红屏）
  const [matrix, setMatrix] = useState<VisibilityMatrix | null>(null);
  const [matrixFailed, setMatrixFailed] = useState(false);
  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null);
  const [selected, setSelected] = useState<{
    topic: string;
    platform: string;
  } | null>(null);
  const [trendPoints, setTrendPoints] = useState<TrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendFailed, setTrendFailed] = useState(false);

  useEffect(() => {
    fetchVisibility();
  }, [selectedCompanyId]);

  const fetchVisibility = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAiVisibility(selectedCompanyId || undefined);
      setItems(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "获取 AI 可见性数据失败",
      );
    } finally {
      setLoading(false);
    }
  };

  /** 全景矩阵（品牌切换重取；钻取参数仅在载入时应用一次）。 */
  useEffect(() => {
    if (!selectedBrandId) {
      setMatrix(null);
      setMatrixFailed(false);
      setSelected(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const m = await getVisibilityMatrix(selectedBrandId, 7);
        if (cancelled) return;
        setMatrix(m);
        setMatrixFailed(false);
        // 初始选中：钻取参数优先，其次首个话题 × 首个平台
        const topic =
          drillTopic && m.topics.includes(drillTopic)
            ? drillTopic
            : m.topics[0];
        const platform =
          drillPlatform && m.platforms.includes(drillPlatform)
            ? drillPlatform
            : m.platforms[0];
        if (topic && platform) setSelected({ topic, platform });
      } catch {
        if (!cancelled) {
          setMatrix(null);
          setMatrixFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId]);

  /** 波动指示（alerts/summary；失败时整个区块隐藏，不影响矩阵）。 */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getAlertSummary(selectedBrandId || undefined);
        if (!cancelled) setAlertSummary(s);
      } catch {
        if (!cancelled) setAlertSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBrandId]);

  /** 选中单元格联动 7 日趋势。 */
  useEffect(() => {
    if (!selected || !selectedBrandId) return;
    let cancelled = false;
    setTrendLoading(true);
    setTrendFailed(false);
    (async () => {
      try {
        const t = await getVisibilityTrend(
          selected.topic,
          selected.platform,
          selectedBrandId,
        );
        if (!cancelled) setTrendPoints(t.points || []);
      } catch {
        if (!cancelled) {
          setTrendPoints([]);
          setTrendFailed(true);
        }
      } finally {
        if (!cancelled) setTrendLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, selectedBrandId]);

  /** 汇总计算。 */
  const summary = useMemo(() => {
    if (items.length === 0) return null;
    const brands = new Set(items.map((i) => i.brand_id));
    const platforms = new Set(items.map((i) => i.platform));
    const avgMention =
      items.reduce((sum, i) => sum + i.mention_rate, 0) / items.length;
    const latestTime = items.reduce(
      (latest, i) => (i.recorded_at > latest ? i.recorded_at : latest),
      items[0].recorded_at,
    );
    // 首推率：仅统计有值的平台综合行（无数据维度由门禁过滤，不参与均值）
    const frRates = items
      .map((i) => i.first_recommend_rate)
      .filter((v): v is number => v != null);
    const avgFirstRecommend =
      frRates.length > 0
        ? frRates.reduce((a, b) => a + b, 0) / frRates.length
        : null;
    // 信源引用量：各平台引用总量求和（全部无数据 → null）
    const citationVals = items
      .map((i) => i.citation_total)
      .filter((v): v is number => v != null);
    const totalCitations =
      citationVals.length > 0
        ? citationVals.reduce((a, b) => a + b, 0)
        : null;
    // 正面情感占比：品牌级情感分布汇总（任一行带分布即可，逐品牌去重）
    const sentimentByBrand = new Map<
      string,
      { positive: number; neutral: number; negative: number }
    >();
    for (const i of items) {
      if (i.sentiment && i.brand_id && !sentimentByBrand.has(i.brand_id)) {
        sentimentByBrand.set(i.brand_id, i.sentiment);
      }
    }
    let pos = 0,
      neu = 0,
      neg = 0;
    for (const s of sentimentByBrand.values()) {
      pos += s.positive;
      neu += s.neutral;
      neg += s.negative;
    }
    const sentimentTotal = pos + neu + neg;
    const positiveRatio =
      sentimentTotal > 0 ? (pos / sentimentTotal) * 100 : null;
    return {
      brandCount: brands.size,
      platformCount: platforms.size,
      avgMention,
      avgFirstRecommend,
      totalCitations,
      positiveRatio,
      latestTime,
      recordCount: items.length,
    };
  }, [items]);

  /** 平静格统计（波动指示第 3 卡：环比波动 <10 的正常格）。 */
  const calmStats = useMemo(() => {
    if (!matrix || matrix.cells.length === 0) return null;
    const total = matrix.cells.length;
    const calm = matrix.cells.filter(
      (c) =>
        c.status === "normal" &&
        (c.prev_score == null ||
          c.score == null ||
          Math.abs(c.score - c.prev_score) < 10),
    ).length;
    return { calm, total };
  }, [matrix]);

  /** 矩阵是否存在上期对比（全部 prev_score 为 null → 首期数据）。 */
  const hasPrevPeriod = useMemo(
    () => !!matrix?.cells.some((c) => c.prev_score != null),
    [matrix],
  );

  /** 趋势图数据（date 裁剪为 MM-DD）。 */
  const trendData = useMemo(
    () =>
      trendPoints.map((p) => ({
        date: p.date.length >= 10 ? p.date.slice(5, 10) : p.date,
        score: p.score,
      })),
    [trendPoints],
  );

  if (loading) {
    return (
      <Box>
        <PageHeader title="AI 可见性 · 全景矩阵" />
        <Loading fullPage />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="AI 可见性 · 全景矩阵"
        breadcrumbs={[{ label: "GEO 分析" }, { label: "AI 可见性" }]}
        subtitle="话题 × 平台 全景矩阵 · 单元格为品牌提及率得分（0–100）"
        actions={
          summary ? (
            <UpdateCapsule
              updatedAt={summary.latestTime}
              sampleN={summary.recordCount}
            />
          ) : undefined
        }
      />

      {/* 波动指示 3 卡（数据源 GET /api/alerts/summary）。
          裁剪口径：客户自主运行洞察 Agent，单期运行下预警恒为 0，
          无 P0/P1 预警时整块不渲染，不留空壳；有预警才出现。 */}
      {alertSummary && (alertSummary.p0_count > 0 || alertSummary.p1_count > 0) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    剧烈波动
                  </Typography>
                  <Badge label={`P0 ×${alertSummary.p0_count}`} variant="error" />
                </Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 600,
                    color:
                      alertSummary.p0_count > 0 ? "error.main" : "text.primary",
                  }}
                >
                  {alertSummary.p0_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                  失联/骤降类 P0 预警数
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    关注
                  </Typography>
                  <Badge label={`P1 ×${alertSummary.p1_count}`} variant="warning" />
                </Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 600,
                    color:
                      alertSummary.p1_count > 0 ? "warning.main" : "text.primary",
                  }}
                >
                  {alertSummary.p1_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                  波动类 P1 预警数 · 本周新增 {alertSummary.week_new} 条
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    平静
                  </Typography>
                  <Badge label={alertSummary.fluctuation || "正常"} variant="success" />
                </Box>
                <Typography sx={{ fontSize: 24, fontWeight: 600 }}>
                  {calmStats ? (
                    <>
                      {calmStats.calm}
                      <Typography
                        component="span"
                        sx={{ fontSize: 14, color: "text.disabled" }}
                      >
                        /{calmStats.total}
                      </Typography>
                    </>
                  ) : (
                    "—"
                  )}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                  话题×平台格中环比波动 &lt;10 的格数
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 钻取态提示（预警 AR1 钻入：topic/platform/highlight=1） */}
      {isDrill && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            px: 2,
            py: 1.5,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderLeft: "3px solid",
            borderLeftColor: "info.main",
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Badge label="钻取态" variant="info" />
          <Typography sx={{ flex: 1, minWidth: 200, fontSize: 13, color: "text.secondary" }}>
            从预警钻入：已自动选中话题
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              「{drillTopic || "—"}」
            </Box>
            ，
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {drillPlatform || "—"}
            </Box>
            列高亮，右侧展示该话题 7 日趋势
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSearchParams({})}
            sx={{ fontSize: 12 }}
          >
            清除过滤
          </Button>
        </Box>
      )}

      {/* 全景矩阵 + 话题趋势 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}
                >
                  话题 × 平台 热力矩阵
                </Typography>
                {matrix && matrix.cells.length > 0 && !hasPrevPeriod && (
                  <Badge label="首期数据" variant="info" />
                )}
                {matrix?.updated_at && (
                  <Typography sx={{ fontSize: 11, color: "text.disabled", ml: "auto" }}>
                    更新于 {formatDateTime(matrix.updated_at)}
                  </Typography>
                )}
              </Box>
              {matrix ? (
                <HeatMatrix
                  topics={matrix.topics}
                  platforms={matrix.platforms}
                  cells={matrix.cells}
                  sortByGap
                  highlightPlatform={
                    isDrill ? drillPlatform || undefined : undefined
                  }
                  selectedTopic={selected?.topic}
                  selectedPlatform={selected?.platform}
                  onCellClick={(topic, platform) =>
                    setSelected({ topic, platform })
                  }
                />
              ) : matrixFailed ? (
                <EmptyState
                  text="矩阵数据暂未就绪"
                  source="运行洞察 Agent 完成探测后自动展示"
                />
              ) : !selectedBrandId ? (
                <EmptyState
                  text="请先在顶部选择品牌"
                  source="矩阵按品牌聚合（brand_id 必填）"
                />
              ) : (
                <Loading />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}
                >
                  {selected ? `「${selected.topic}」7 日趋势` : "话题 7 日趋势"}
                </Typography>
                {selected && <Badge label={selected.platform} variant="info" />}
              </Box>
              {!selected ? (
                <EmptyState text="点击矩阵单元格查看该话题 7 日趋势" />
              ) : trendLoading ? (
                <Loading />
              ) : trendFailed ? (
                <EmptyState
                  text="趋势数据暂未就绪"
                  source="由洞悉引擎周期探测产出，趋势端点接入后自动展示"
                />
              ) : trendData.length < 2 ? (
                <EmptyState
                  text="首期数据，暂无趋势可比"
                  source="再次运行洞察 Agent 产生新一期数据后，此处自动展示变化趋势"
                />
              ) : (
                <Box sx={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <LineChart
                      data={trendData}
                      margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={muiTheme.palette.lineSoft}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{
                          fontSize: 11,
                          fill: muiTheme.palette.text.disabled,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{
                          fontSize: 11,
                          fill: muiTheme.palette.text.disabled,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <ChartTooltip
                        formatter={(value) => [`${value ?? "—"} 分`, "提及率得分"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke={muiTheme.palette.primary.main}
                        strokeWidth={2}
                        dot={{ r: 2.5 }}
                        activeDot={{ r: 4 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error ? (
        <Card>
          <CardContent>
            <EmptyState text={error} />
          </CardContent>
        </Card>
      ) : !summary ? (
        <Card>
          <CardContent>
            <EmptyState
              text="暂无 AI 可见性汇总数据"
              source="运行洞察 Agent 完成探测后自动展示"
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* 保留层 1：汇总卡片（PRD §2.2.1）
              门禁 §13.3：BAVS 总分/评级无真实数据源（五维中三维未接入），
              恒"待产出"属伪装指标卡 → 不渲染，仅保留真实数据卡。 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                label="覆盖品牌"
                value={String(summary.brandCount)}
                sub={`${summary.platformCount} 个 AI 平台`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                label="最近探测"
                value={formatDateTime(summary.latestTime).split(" ")[0] || "—"}
                sub={`${summary.recordCount} 条记录`}
              />
            </Grid>
          </Grid>

          {/* 保留层 2：BAVS 维度表现（PRD §2.2.1；门禁 §13.3：仅渲染已接入维度） */}
          <Card>
            <CardContent>
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", mb: 1 }}
              >
                BAVS 维度表现
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 2 }}>
                BAVS 口径：品牌提及率×25% + 答案份额（首推率）×25% + 信源引用×20% +
                情感占比×15% + 排名位置×15%（排名位置无数据源，不展示）
              </Typography>
              <Grid container spacing={2}>
                {BAVS_DIMENSIONS.map((dim) => {
                  // 0–100 评分维度用色阶；信源引用量为计数维度，不参与色阶
                  const metric: { value: number | null; isScore: boolean } =
                    dim.name === "品牌提及率"
                      ? { value: summary.avgMention, isScore: true }
                      : dim.name === "首推率（答案份额）"
                        ? { value: summary.avgFirstRecommend, isScore: true }
                        : dim.name === "信源引用量"
                          ? { value: summary.totalCitations, isScore: false }
                          : { value: summary.positiveRatio, isScore: true };
                  // 门禁 §13.3：该维度本轮无真实数据 → 不渲染该卡
                  if (metric.value === null) return null;
                  const color = metric.isScore
                    ? getScoreColor(metric.value)
                    : "text.primary";
                  return (
                    <Grid item xs={12} sm={6} md={3} key={dim.name}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1.5,
                          px: 1.5,
                          borderRadius: 1,
                          bgcolor: "brand.50",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: "text.primary",
                            }}
                          >
                            {dim.name}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                            权重 {dim.weight}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{ fontSize: 16, fontWeight: 600, color }}
                        >
                          {metric.isScore
                            ? formatScore(metric.value)
                            : `${metric.value} 条`}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              <Typography
                sx={{ fontSize: 11, color: "text.disabled", mt: 2 }}
              >
                · 四维均来自当前探测数据：提及率/首推率/引用量取自平台综合行，
                情感占比取自问句级明细的情感标注；某维度本轮无数据则不渲染该卡
                · 强弱状态：红&lt;60 / 橙 60-80 / 绿≥80（引用量为计数，不参与色阶）
              </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
